import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import ActionButton from "../buttons/ActionButton";
import Input from "../Input";
import { useFetch } from "utils/query/query";
import BillingCustomerMatches, {
  BillingNameMatchResult,
} from "./BillingCustomerMatches";

const LOOKUP_DELAY_MS = 400;
const MIN_LOOKUP_LENGTH = 2;

export default function AddOrganizationDialog({
  handleAdd,
  handleClose,
  progressPending = false,
}: {
  handleAdd: (organizationName: string, billingCustomerId?: string) => void;
  handleClose: () => void;
  progressPending?: boolean;
}) {
  const [organizationName, setOrganizationName] = useState<string>("");
  const [lookupName, setLookupName] = useState<string>("");
  const [billingCustomerId, setBillingCustomerId] = useState<string | null>(
    null
  );

  const typedName = organizationName.trim();

  useEffect(() => {
    const timer = setTimeout(() => setLookupName(typedName), LOOKUP_DELAY_MS);
    return () => clearTimeout(timer);
  }, [typedName]);

  useEffect(() => {
    setBillingCustomerId(null);
  }, [typedName]);

  const lookupSettled = lookupName === typedName;
  const lookupReady = lookupName.length >= MIN_LOOKUP_LENGTH;
  const { data: matches, isLoading } = useFetch<BillingNameMatchResult>(
    `/billingCustomerNameMatches?name=${encodeURIComponent(lookupName)}`,
    { paramsAvailable: lookupReady, retry: false, keepPreviousData: false }
  );

  return (
    <Dialog fullWidth maxWidth="sm" open onClose={handleClose}>
      <DialogTitle>Add Organization</DialogTitle>
      <DialogContent>
        <Box mt={4}>
          <Input
            onChange={(value) => {
              setOrganizationName(value);
            }}
            onBlur={(value) => {
              setOrganizationName(value);
            }}
            label="Organization Name"
            fullWidth
            required
            helperText={"Name of the organization"}
          />
          {lookupReady && (
            <BillingCustomerMatches
              result={matches}
              loading={!lookupSettled || isLoading}
              selectedCustomerId={billingCustomerId}
              onSelect={setBillingCustomerId}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleClose}
          style={{ margin: 8, fontSize: 14, textTransform: "none" }}
          sx={{
            display: "inline",
            mx: 0.5,
          }}
        >
          Cancel
        </Button>
        <ActionButton
          variant="contained"
          color="primary"
          progress={progressPending}
          onClick={() => handleAdd(typedName, billingCustomerId ?? undefined)}
          style={{ margin: 8, fontSize: 14, textTransform: "none" }}
          sx={{
            display: "inline",
            mx: 0.5,
          }}
        >
          Add Organization
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}
